// Learner activities: one lesson, taught to one section, in one iteration — what a teacher means
// by "this assignment", and what Student Learning is organised around (docs/STUDENT_LEARNING.md).
// Read from GET /api/activities, which the database scopes to the caller's own classes.

import type { Scope } from "./components/AssignmentHome";

export interface Activity {
  activity_id: string;
  lesson_id: string;
  lesson_title: string;
  unit_id: string | null;
  unit_title: string | null;
  lesson_ordinal: number | null;
  section_id: string;
  section_label: string | null;
  iteration: string;
  window_label: string | null;
  opens: string | null;
  due: string | null;
  task_id: string | null;
  // False for a lesson with no task: taught, not scored. Normal — never shown as missing data.
  scored: boolean;
}

export interface District {
  tenant_id: string;
  name: string;
}

export interface ActivitiesResponse {
  available: boolean;
  activities: Activity[];
  // The school system this teacher's classes belong to — what the top bar shows. Null when the
  // district has no display name, which shows nothing rather than an identifier.
  district?: District | null;
}

/** Activities grouped by unit, in the order the server sent them (unit, then lesson order). */
export function byUnit(list: Activity[]): { unit: string; activities: Activity[] }[] {
  const groups: { unit: string; activities: Activity[] }[] = [];
  for (const a of list) {
    const unit = a.unit_title ?? "Other lessons";
    const g = groups.find((x) => x.unit === unit);
    if (g) g.activities.push(a);
    else groups.push({ unit, activities: [a] });
  }
  return groups;
}

/**
 * The review console's scope for an activity: the cohort, the task and the iteration a paper is
 * bound to. An unscored activity has no task, so nothing is scored under it — its scope still
 * names the cohort, and the console correctly shows that nothing is waiting.
 */
export function scopeOf(a: Activity): Scope {
  return { section_id: a.section_id, task_id: a.task_id, iteration: a.iteration,
           window_label: a.window_label };
}

/** "Period 4 · ELA 11B · Final" — the line under a lesson's title. */
export function activityLine(a: Activity): string {
  const iter = a.iteration.charAt(0).toUpperCase() + a.iteration.slice(1);
  return [a.section_label ?? a.section_id, iter].join(" · ");
}

// The last activity is remembered for the SESSION (docs/STUDENT_LEARNING.md), not the device: a shared machine
// shouldn't reopen on someone else's class tomorrow.
const LAST_KEY = "writing.activity";

export function lastActivity(): string | null {
  try { return sessionStorage.getItem(LAST_KEY); } catch { return null; }
}

export function rememberActivity(id: string): void {
  try { sessionStorage.setItem(LAST_KEY, id); } catch { /* storage blocked: harmless */ }
}

/** Which activity to open: the one asked for if it's visible, else the last used, else the first. */
export function chooseActivity(list: Activity[], asked: string | undefined): Activity | null {
  return list.find((a) => a.activity_id === asked)
    ?? list.find((a) => a.activity_id === lastActivity())
    ?? list[0]
    ?? null;
}
